import { BUTTON_SIZE, BUTTON_TYPE, COLORS, DSButton } from '@repo/design-system/index'
import { QIcon_bar_graph, QIcon_book, QIcon_person, QIcon_topic } from '@repo/icons/index'
import { useState } from 'react'
import { useAvailablePricings } from '../../../modules/apis/ai_tutor/queries'
import { CUSTOMER_TYPE_SLUGS } from '../../../modules/apis/sanity'
import { useCurrentUser } from '../../../modules/context/UserContext'
import { APP_SCREEN_NAME } from '../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useIsInApp from '../../../modules/hooks/common/useIsInApp'
import useIsMobile from '../../../modules/hooks/common/useIsMobile'
import useIsWebview from '../../../modules/hooks/common/useIsWebview'
import useReturnFullUrl from '../../../modules/hooks/webview/common/useReturnFullUrl'
import { n_pricing, n_subscribe, s_pricing } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import urls from '../../../modules/urls'
import { COUNTRY_CURRENCY } from '../../../modules/vars'
import BillingTypeSelector from './BillingTypeSelector'
import PricingFaqSection from './PricingFaqSection'
import { AITutorPricingSwitchCurrency } from './PricingSwitchCurrency'

export interface IPricingData {
  id: number
  billing_period_unit: string
  billing_period_number: number
  platform: string
  name: string
  original_prices: Prices
  monthly_original_prices: Prices
  sale_prices: Prices
  monthly_prices: Prices
  permissions: Permissions
}

export interface Prices {
  KRW: string
  USD: string
  CNY: string
  JPY: string
  TWD: string
  VND: string
}

export interface Permissions {
  analysis: boolean
  roleplay: boolean
  curriculum: boolean
  discussion: boolean
}

export function extractNumber(price) {
  // 정규식을 사용해 숫자와 소수점만 남기고 제거
  if (!price) return

  return price.replace(/[^\d.]/g, '')
}

const percentage = (pricingData, currency) => {
  const isKRW = currency === 'KRW'
  const isUSD = currency === 'USD'
  const isOtherCurrency = !isKRW && !isUSD

  const originalPrice = extractNumber(pricingData?.original_prices[isOtherCurrency ? 'USD' : currency])
  const salePrice = extractNumber(pricingData?.sale_prices[isOtherCurrency ? 'USD' : currency])
  const discountPercentage = Math.floor(((originalPrice - salePrice) / originalPrice) * 100)
  return discountPercentage + '%'
}

// 여기는 구매 리스트 -> 구매 버튼 클릭
const PricingAITutorMembership = ({ isB2B = false }) => {
  const router = useAppRouter()
  const { currentUser } = useCurrentUser()
  const { t, isLocaleKO } = useTranslation()
  const getFullUrl = useReturnFullUrl()
  const isMobile = useIsMobile()
  const isInApp = useIsInApp()
  const isWebview = useIsWebview()

  const [billingType, setBillingType] = useState('YEAR')
  const [currency, setCurrency] = useState(COUNTRY_CURRENCY.KRW)

  const { data, isLoading } = useAvailablePricings(isB2B ? 'b2b2c' : 'web', billingType, null) // Ensure that platform has a value

  const handlePurchaseClick = (pricingData) => {
    const detailUrl =
      urls.student.newPortal.pricing.aiTutor.detail(pricingData?.id) +
      `?country_code=${currency == 'KRW' ? 'KR' : 'US '}`

    if (currentUser) {
      if (isWebview && !isInApp) {
        router.push(detailUrl, {
          appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
          appSendData: {
            url: getFullUrl(detailUrl),
          },
        })
        return
      }

      router.push(detailUrl)
      return
    }

    router.push(`${urls.student.auth.new}?redirect_url=/${detailUrl}`)
  }

  if (isLoading) return null

  const renderUsingPeriod = (pricingData) => {
    if (pricingData) {
      const billingPeriodUnit = pricingData.billing_period_unit
      const billingPeriodNumber = pricingData.billing_period_number

      if (isLocaleKO) {
        if (billingPeriodUnit === 'YEAR') {
          return `${billingPeriodNumber}년`
        } else if (billingPeriodUnit === 'MONTH') {
          return `${billingPeriodNumber}개월`
        } else if (billingPeriodUnit === 'DAY') {
          return `${billingPeriodNumber}일`
        }
      } else {
        return `${billingPeriodNumber} ${billingPeriodUnit}`
      }
    }
  }

  const pricingData: IPricingData[] = data?.pricings || []

  const premiumProductIndex = isB2B ? 0 : pricingData.length - 1

  let firstPricingData: IPricingData
  let secondPricingData: IPricingData
  if (isB2B) {
    firstPricingData = pricingData[premiumProductIndex]
    secondPricingData = pricingData[1]
  } else {
    firstPricingData = pricingData.find((x: any) => x.plan_key === 'premium_plus')
    secondPricingData = pricingData.find((x: any) => x.plan_key === 'basic')
  }

  const isKRW = currency === 'KRW'
  const isUSD = currency === 'USD'
  const isOtherCurrency = !isKRW && !isUSD

  const firstPricingDiscountRate = percentage(firstPricingData, currency)
  const secondPricingDiscountRate = percentage(secondPricingData, currency)

  const isYearly = billingType === 'YEAR'
  const premiumDiscountPrice = isB2B
    ? isYearly
      ? isLocaleKO
        ? '20,000'
        : '15'
      : isLocaleKO
      ? '19,000'
      : '14.61'
    : isLocaleKO
    ? '150,000'
    : '115'
  const premiumDiscountPriceLabel = isLocaleKO ? `${premiumDiscountPrice}원` : `$${premiumDiscountPrice}`
  const basicDiscountPrice = isB2B ? (isLocaleKO ? '5,000' : '3.84') : null
  const basicDiscountPriceLabel = isLocaleKO ? `${basicDiscountPrice}원` : `$${basicDiscountPrice}`

  const premiumOriginalPrice = firstPricingData?.original_prices[isOtherCurrency ? 'USD' : currency]
  const premiumTotalPrice = firstPricingData?.sale_prices[isOtherCurrency ? 'USD' : currency]
  const premiumTotalPriceLabel = isKRW && isLocaleKO ? premiumTotalPrice.replace('₩', '') + '원' : premiumTotalPrice

  const basicOriginalPrice = secondPricingData?.original_prices[isOtherCurrency ? 'USD' : currency]
  const basicTotalPrice = secondPricingData?.sale_prices[isOtherCurrency ? 'USD' : currency]
  const basicTotalPriceLabel = isKRW && isLocaleKO ? basicTotalPrice.replace('₩', '') + '원' : basicTotalPrice

  return (
    <div className="px-[16px] sm:mx-auto">
      <div className="max-w-[780px] mx-auto">
        <div className={`mb-32 flex sm:flex-row flex-col gap-y-16 ${isB2B ? 'justify-between' : 'sm:justify-end'}`}>
          {isB2B && (
            <BillingTypeSelector maxDiscountRate={'73%'} selectedType={billingType} setSelectedType={setBillingType} />
          )}
          <div className="max-w-[89px]">
            <AITutorPricingSwitchCurrency setCurrency={setCurrency} currency={currency} />
          </div>
        </div>
        {!isB2B && !isKRW && isMobile && (
          <ul className="pt-16 pb-20">
            <li className="Body2-14Md text-gray-500">*{t(s_pricing.notice_usd)}</li>
            <li className="Body2-14Md text-gray-500">*{t(s_pricing.notice_exchange)}</li>
          </ul>
        )}
        <div className="flex flex-col sm:flex-row gap-[16px] w-full mt-[16px]">
          {/* 프리미엄 플러스 */}
          <div className="border-1 rounded-[8px] relative py-[20px] px-[16px] w-full flex flex-col justify-between">
            {/* Badge */}
            <div className="absolute flex flex-row gap-x-[4px] top-[-10px] left-[8px]">
              <PricingPackage title={t(s_pricing.card_popular)} />
              {isB2B && premiumDiscountPrice && (
                <PricingPackage title={t(n_pricing.ai_option.sale_amount(premiumDiscountPriceLabel))} />
              )}
              {!isB2B && premiumDiscountPrice && (
                <PricingPackage title={t(s_pricing.benefit_list2(premiumDiscountPriceLabel))} />
              )}
            </div>
            <div className="">
              {/* 가격 정보 : 프리미엄 플럿, 가격, 기간 등 */}
              <div className="flex flex-row justify-between">
                <div className="flex flex-col">
                  <span className="text-[20px] font-bold text-gray-800 leading-[28px]">
                    {firstPricingData?.name || ''}
                  </span>
                  <span className="Body2-14Md text-gray-500">{renderUsingPeriod(firstPricingData)}</span>
                </div>
                <div className="">
                  <div className="flex flex-row gap-x-[4px] justify-end w-full">
                    <span className="Body2-14Bd text-red-500">{firstPricingDiscountRate}</span>
                    <span className="Body2-14Md text-gray-400 line-through">{premiumOriginalPrice}</span>
                  </div>
                  <div className="flex justify-end">
                    <span className="text-gray-800 Title1-20Bd">{premiumTotalPriceLabel}</span>
                  </div>
                  <div className="flex justify-end">
                    <div className="flex flex-col ">
                      {!isKRW && !isUSD && (
                        <span className="text-gray-500 Body2-14Md text-right">
                          ({firstPricingData?.sale_prices[currency]})
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <hr className="my-[20px]" />
              {/* 패키지 기능 리스트 */}
              <div className="flex flex-col gap-y-[8px]">
                {firstPricingData?.permissions?.curriculum && (
                  <div className="flex flex-row items-center gap-x-[8px] text-gray-700 Body2-14Md">
                    <QIcon_book width={20} height={20} color={COLORS.GRAY400} />
                    <span className="">{t(n_subscribe.select.benefit_1)}</span>
                  </div>
                )}
                {firstPricingData?.permissions?.roleplay && (
                  <div className="flex flex-row items-center gap-x-[8px] text-gray-700 Body2-14Md">
                    <QIcon_person width={20} height={20} color={COLORS.GRAY400} />
                    <span className="">{t(n_subscribe.select.benefit_2)}</span>
                  </div>
                )}
                {firstPricingData?.permissions?.discussion && (
                  <div className="flex flex-row items-center gap-x-[8px] text-gray-700 Body2-14Md">
                    <QIcon_topic width={20} height={20} color={COLORS.GRAY400} />
                    <span className="">{t(n_subscribe.select.benefit_3)}</span>
                  </div>
                )}
                {firstPricingData?.permissions?.analysis && (
                  <div className="flex flex-row items-center gap-x-[8px] text-gray-700 Body2-14Md">
                    <QIcon_bar_graph width={20} height={20} color={COLORS.GRAY400} />
                    <span className="">{t(n_subscribe.select.benefit_4)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* 구매 버튼 */}
            <div className="mt-[16px]">
              <DSButton
                type={BUTTON_TYPE.SOLID}
                size={BUTTON_SIZE.MD}
                onClick={() => handlePurchaseClick(firstPricingData)}
              >
                <DSButton.Label>{t(n_subscribe.tab.btn_pay)}</DSButton.Label>
              </DSButton>
            </div>
          </div>
          {/* 베이직 */}
          <div className="border-1 rounded-[8px] relative py-[20px] px-[16px] w-full flex flex-col justify-between">
            <div className="absolute flex flex-row gap-x-[4px] top-[-10px] left-[8px]">
              {basicDiscountPrice && isB2B && (
                <PricingPackage title={t(n_pricing.ai_option.sale_amount(basicDiscountPriceLabel))} />
              )}
            </div>
            <div className="">
              {/* 가격 정보 : 프리미엄 플럿, 가격, 기간 등 */}
              <div className="flex flex-row justify-between">
                <div className="flex flex-col">
                  <span className="text-[20px] font-bold text-gray-800 leading-[28px]">
                    {secondPricingData?.name || ''}
                  </span>
                  <span className="Body2-14Md text-gray-500">{renderUsingPeriod(secondPricingData)}</span>
                </div>
                <div>
                  <div className="flex flex-row gap-x-[4px] justify-end w-full">
                    <span className="Body2-14Bd text-red-500">{secondPricingDiscountRate}</span>
                    <span className="Body2-14Md text-gray-400 line-through">{basicOriginalPrice}</span>
                  </div>
                  <div className="flex justify-end">
                    <span className="text-gray-800 Title1-20Bd">{basicTotalPriceLabel}</span>
                  </div>
                  <div className="flex justify-end">
                    <div className="flex flex-col ">
                      {!isKRW && !isUSD && (
                        <span className="text-gray-500 Body2-14Md text-right">
                          ({secondPricingData?.sale_prices[currency]})
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <hr className="my-[20px]" />
              {/* 패키지 기능 리스트 */}
              <div className="flex flex-col gap-y-[8px]">
                {secondPricingData?.permissions?.curriculum && (
                  <div className="flex flex-row items-center gap-x-[8px] text-gray-700 Body2-14Md">
                    <QIcon_book width={20} height={20} color={COLORS.GRAY400} />
                    <span className="">{t(n_subscribe.select.benefit_1)}</span>
                  </div>
                )}
                {secondPricingData?.permissions?.roleplay && (
                  <div className="flex flex-row items-center gap-x-[8px] text-gray-700 Body2-14Md">
                    <QIcon_person width={20} height={20} color={COLORS.GRAY400} />
                    <span className="">{t(n_subscribe.select.benefit_2)}</span>
                  </div>
                )}
                {secondPricingData?.permissions?.discussion && (
                  <div className="flex flex-row items-center gap-x-[8px] text-gray-700 Body2-14Md">
                    <QIcon_topic width={20} height={20} color={COLORS.GRAY400} />
                    <span className="">{t(n_subscribe.select.benefit_3)}</span>
                  </div>
                )}
                {secondPricingData?.permissions?.analysis && (
                  <div className="flex flex-row items-center gap-x-[8px] text-gray-700 Body2-14Md">
                    <QIcon_bar_graph width={20} height={20} color={COLORS.GRAY400} />
                    <span className="">{t(n_subscribe.select.benefit_4)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* 구매 버튼 */}
            <div className="mt-[16px]">
              <DSButton
                customBorderColor={COLORS.PURPLE600}
                type={BUTTON_TYPE.OUTLINE}
                size={BUTTON_SIZE.MD}
                onClick={() => handlePurchaseClick(secondPricingData)}
              >
                <DSButton.Label customTxtColor={COLORS.PURPLE600}>{t(n_subscribe.tab.btn_pay)}</DSButton.Label>
              </DSButton>
            </div>
          </div>
        </div>
        {!isB2B && !isKRW && !isMobile && (
          <ul className="mt-16">
            <li className="Body2-14Md text-gray-500">*{t(s_pricing.notice_usd)}</li>
            <li className="Body2-14Md text-gray-500">*{t(s_pricing.notice_exchange)}</li>
          </ul>
        )}
      </div>

      <div className="mt-[16px] flex flex-col gap-y-[16px]">
        {/* FAQ */}
        <div className="mb-[40px] sm:mb-[0px]">
          <PricingFaqSection slug={CUSTOMER_TYPE_SLUGS.B2C_AI_TUTOR} className="!px-[0px] sm:!p-[32px]" />
        </div>
      </div>
    </div>
  )
}

export default PricingAITutorMembership

const PricingPackage = ({ title }) => {
  if (title) {
    return (
      <div className="rounded-[4px] bg-blue-50 text-blue-500 border-1 border-blue-200 Caption-12Bd px-[6px]">
        {title}
      </div>
    )
  } else {
    return null
  }
}
